import scrollLock from 'scroll-lock';
import { gsap } from 'gsap';

let menu = null;

export function getMenu() {
	return menu;
}

export class Menu {
	constructor(container) {
		this.hamburger = container.querySelector('[data-hamburger]');
		this.nav = container.querySelector('[data-nav]');
		this.navLogo = container.querySelector('[data-nav-logo');
		this.menuIsOpen = false;

		this.contactLink = container.querySelector('a[href="#contact"]');
		this.contactSection = container.querySelector('[data-contact]');

		this.headerSection = container.querySelector('[data-header-section]');
		if (this.headerSection) {
			this.nav.classList.add('header');
			this.sectionBottom = this.headerSection.getBoundingClientRect().bottom;
		}

		this.hamburger.addEventListener('click', (e) => {
			this.toggleMenu();
		});

		this.prevScrollY = window.scrollY;

		window.addEventListener('scroll', () => {
			this.onScroll();
		});

		this.resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				if (entry.contentRect.width > 768 && this.menuIsOpen) {
					this.close();
					this.menuIsOpen = false;
					scrollLock.enablePageScroll();
				}
			}
		});

		this.resizeObserver.observe(document.body);

		if (this.contactSection) {
			this.contactLink.addEventListener('click', (e) => {
				e.preventDefault();
				this.close();
				this.menuIsOpen = false;
				this.contactSection.scrollIntoView({ behavior: 'smooth' });
			});
		}
	}

	static init() {
		document.addEventListener('bless', (e) => {
			if (!e.target) {
				return;
			}

			if (menu) {
				menu.destroy();
			}

			menu = new Menu(e.target);
		});

		document.addEventListener('close-menu', (e) => {
			if (menu) {
				menu.close();
			}
		});

		if (this.menuIsOpen === false) {
			scrollLock.enablePageScroll();
		}
	}

	toggleMenu() {
		if (this.menuIsOpen) {
			this.close();
		} else {
			this.open();
		}
		this.menuIsOpen = !this.menuIsOpen;
		this.onScroll();
	}

	open() {
		scrollLock.disablePageScroll();
		this.hamburger.classList.add('is-active');
		document.querySelector('body').classList.add('menu-open');
		this.trapFocus();
		this.hamburger.tabIndex = 0;
		this.hamburger.ariaLabel = 'button - sluit het menu';
	}

	close() {
		return new Promise((resolve) => {
			this.hamburger.classList.remove('is-active');
			document.querySelector('body').classList.remove('menu-open');
			this.removeTrapFocus();
			this.hamburger.tabIndex = 1;
			this.hamburger.ariaLabel = 'button - open het menu';
			
			setTimeout(() => {
				resolve();
			}, 800);
			scrollLock.enablePageScroll();
		});
	}

	onScroll() {
		const currentScrollY = window.scrollY;
		const navHeight = this.nav.offsetHeight;
	
		if (currentScrollY > this.prevScrollY && currentScrollY >= navHeight) {
			this.handleScrollDown();
		} else if (currentScrollY < this.prevScrollY) {
			this.handleScrollUp();
		}

		this.prevScrollY = currentScrollY;
	}

	handleScrollDown() {
		gsap.to(this.nav, {
			yPercent: -100,
			duration: 1,
			ease: 'power4.out',
			delay: 0.2
		})
	}
	
	handleScrollUp() {
		gsap.to(this.nav, {
			yPercent: 0,
			duration: 1,
			ease: 'power4.out',
			delay: 0.2
		})
	}

	trapFocus() {
		const focusableEls = this.nav.querySelectorAll(
			'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])'
		);
		this.firstFocusableEl = focusableEls[0];
		this.lastFocusableEl = focusableEls[focusableEls.length - 1];

		this.tabfocussed = (event) => this.tabFocus(event);
		this.nav.addEventListener('keydown', this.tabfocussed);
	}

	tabFocus(e) {
		const KEYCODE_TAB = 9;
		const isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB;

		if (!isTabPressed) {
			return;
		}

		if (e.shiftKey) {
			if (document.activeElement === this.firstFocusableEl) {
				this.lastFocusableEl.focus();
				e.preventDefault();
			}
		} else {
			if (document.activeElement === this.lastFocusableEl) {
				this.firstFocusableEl.focus();
				e.preventDefault();
			}
		}
	}

	removeTrapFocus(element) {
		this.nav.removeEventListener('keydown', this.tabfocussed);
	}

	destroy() {
		this.resizeObserver.disconnect();
		window.removeEventListener('scroll', this.boundOnScroll);
	}
}
